// frontend/src/services/localRecording/uploader.js
//
// Bakgrundsuppladdare för lokal-först-inspelning. Tömmer IndexedDB-kön:
// laddar upp chunkar i chunk_index-ordning per session, raderar EN chunk först
// vid HTTP 2xx-kvittens (delete-on-confirm). Backoff vid fel. När en session är
// klar lokalt OCH dess kö är tömd → complete mot servern.
//
// Väcks av: intervall-tick, `online`-event, nya chunkar (wake()) och app-start.

import apiClient from '../../apiClient';
import {
  getUnconfirmedChunks,
  getSession,
  getAllSessions,
  getChunksForSession,
  updateChunk,
  deleteChunk,
  setServerSessionId,
  setSessionOwner,
  deleteSessionAndChunks,
  getPendingCountsBySession,
} from './db';
import { isRecordingBusy } from '../recordingActivity';

const UPLOAD_ENDPOINT = '/recorder/sessions/upload_chunk'; // v2, upsert på client_session_id
const MAX_ATTEMPTS = 20;
const TICK_MS = 5000;
// Obekräftade klipp får ligga kvar i webbläsaren högst så här länge (oavsett state).
export const LOCAL_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Backoff: 2s, 4s, 8s, 16s, 30s (tak)
function backoffMs(attempts) {
  return Math.min(2000 * 2 ** Math.max(0, attempts - 1), 30000);
}

function isPermanentHttp(status) {
  // 4xx (utom 408/429) = klientfel som inte blir bättre av omförsök.
  return status && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

// Servern går inte att nå alls: nätfel/timeout utan svar, eller proxyn svarar
// 502/503/504 medan backenden startar om. Räknas inte mot MAX_ATTEMPTS, klippet
// väntar i stället tills servern är tillbaka.
function isUnreachable(status) {
  return !status || status === 502 || status === 503 || status === 504;
}

// Serverfel går över av sig själva (t.ex. databasen tillfälligt nere): klippet ger
// inte upp utan försöks igen, högst LOCAL_MAX_AGE_MS.
function isTemporary(status) {
  return isUnreachable(status) || status >= 500;
}

// Inloggningen duger inte: 401 (utgången/saknad token) eller 422 (ogiltig token,
// endpointerna här svarar aldrig 422 själva). Inget fel på klippet: det väntar
// utan att räknas mot MAX_ATTEMPTS och försöks igen efter ny inloggning.
function isAuthFailure(status) {
  return status === 401 || status === 422;
}

let running = false;      // single-flight
let wantAnother = false;  // en wake kom in medan vi körde
let timer = null;
let rejectedToken = null; // token som servern underkänt, inga anrop förrän en ny finns
const listeners = new Set(); // callbacks: (event) => void

function emit(event) {
  for (const l of listeners) {
    try { l(event); } catch (_) { /* ignorera lyssnarfel */ }
  }
}

// Prenumerera på uppladdar-händelser:
//  { type: 'confirmed', clientSessionId, chunkIndex, text }
//  { type: 'permanent_failure', clientSessionId, chunkIndex, error }
//  { type: 'session_completed', clientSessionId, serverSessionId }
//  { type: 'pending_changed' }
//  { type: 'login_required' } (varje varv så länge inloggningen inte duger)
export function subscribeUploader(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function currentToken() {
  return localStorage.getItem('access_token');
}

function noteLoginRequired(err) {
  rejectedToken = err.config?.headers?.Authorization?.replace('Bearer ', '') || currentToken();
}

function waitingForLogin() {
  const token = currentToken();
  return !token || token === rejectedToken;
}

// Användar-id ur tokenens payload (går att läsa även när tokenen gått ut).
function currentUserId() {
  try {
    const payload = currentToken().split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return String(JSON.parse(atob(payload)).sub);
  } catch (_) {
    return null;
  }
}

// Servern kopplar klipp till den inloggade användaren. Klipp som väntar på
// inloggning får därför bara laddas upp som den som spelade in dem, annars
// hamnar ljudet på en kollegas konto när någon annan loggar in på samma dator.
// Stämplar nya sessioner med ägare och returnerar id:n för andra användares.
async function claimSessions() {
  const me = currentUserId();
  const foreign = new Set();
  for (const s of await getAllSessions()) {
    if (!s.owner_user_id) {
      if (me) await setSessionOwner(s.client_session_id, me);
    } else if (s.owner_user_id !== me) {
      foreign.add(s.client_session_id);
    }
  }
  return foreign;
}

// Som getPendingCountsBySession, men utan andra användares sessioner.
export async function getOwnPendingCounts() {
  const [counts, sessions] = await Promise.all([getPendingCountsBySession(), getAllSessions()]);
  const me = currentUserId();
  for (const s of sessions) {
    if (s.owner_user_id && s.owner_user_id !== me) delete counts[s.client_session_id];
  }
  return counts;
}

async function uploadOne(chunk) {
  const session = await getSession(chunk.client_session_id);
  if (!session) {
    // Ingen session-post: radera inte tyst. 24 h-gränsen tar klippet och rapporterar det.
    return;
  }

  const form = new FormData();
  form.append('file', chunk.blob, `chunk-${chunk.chunk_index}.wav`);
  form.append('client_session_id', chunk.client_session_id);
  form.append('chunk_index', String(chunk.chunk_index));
  form.append('is_final', chunk.is_final ? 'true' : 'false');
  if (session.template_id != null) form.append('template_id', String(session.template_id));
  if (session.session_name) form.append('session_name', session.session_name);

  await updateChunk(chunk.id, { upload_state: 'uploading' });

  try {
    const resp = await apiClient.post(UPLOAD_ENDPOINT, form, { timeout: 60000 });

    // Lazy-skapad server-session: spara mappningen.
    const serverId = resp.data?.server_session_id;
    if (serverId && !session.server_session_id) {
      await setServerSessionId(chunk.client_session_id, serverId);
    }

    // Extrahera ev. deltext (samma format som gamla transcribeChunk).
    let text = '';
    const tr = resp.data?.transcription;
    if (typeof tr === 'string') text = tr;
    else if (tr && typeof tr === 'object') text = tr.corrected_transcript || tr.raw_transcript || '';

    // Delete-on-confirm.
    await deleteChunk(chunk.id);
    emit({ type: 'confirmed', clientSessionId: chunk.client_session_id, chunkIndex: chunk.chunk_index, text });
    emit({ type: 'pending_changed' });
    return;
  } catch (err) {
    const status = err.response?.status;
    const attempts = (chunk.attempts || 0) + 1;

    if (isAuthFailure(status)) {
      noteLoginRequired(err);
      await updateChunk(chunk.id, { upload_state: 'pending', last_error: err.message, next_attempt_at: 0 });
      throw err;
    }

    // Servern svarar men tar inte emot ett klipp som redan passerat 24 h: ge upp, så att
    // det inte blir kvar för alltid (nåbar server är då inte längre skälet att vänta).
    const tooOldToRetry = status >= 500 && !isUnreachable(status)
      && chunk.created_at && chunk.created_at < Date.now() - LOCAL_MAX_AGE_MS;

    if (isPermanentHttp(status) || tooOldToRetry || (!isTemporary(status) && attempts >= MAX_ATTEMPTS)) {
      await updateChunk(chunk.id, {
        upload_state: 'permanent_failure',
        attempts,
        last_error: err.response?.data?.message || err.message,
      });
      emit({
        type: 'permanent_failure',
        clientSessionId: chunk.client_session_id,
        chunkIndex: chunk.chunk_index,
        error: err.response?.data?.message || err.message,
      });
      // Rapportera metadata till backend så admin ser det (audio finns bara lokalt).
      reportPermanentFailure(session, chunk, err).catch(() => {});
      emit({ type: 'pending_changed' });
      return;
    }

    // Övergående fel → backoff, försök igen senare.
    await updateChunk(chunk.id, {
      upload_state: 'pending',
      attempts,
      last_error: err.message,
      next_attempt_at: Date.now() + backoffMs(attempts),
    });
    throw err; // signalera till drainOnce att denna session inte kan gå vidare nu
  }
}

async function reportPermanentFailure(session, chunk, err) {
  try {
    await apiClient.post('/api/errors/failed_chunk', {
      client_session_id: chunk.client_session_id,
      server_session_id: session.server_session_id || null,
      chunk_index: chunk.chunk_index,
      attempts: chunk.attempts,
      last_error: err.response?.data?.message || err.message,
      timestamp: Date.now(),
    });
  } catch (_) { /* best effort */ }
}

// 24 h-gräns: obekräftade klipp (oavsett state) äldre än LOCAL_MAX_AGE_MS raderas
// ur webbläsaren. Metadata (inte ljud) rapporteras först till backend, en rapport
// per session. Raderingen väntar om rapporten inte når fram (utgången inloggning,
// server som inte går att nå eller har problem), så att rapporten inte går förlorad.
// Ett klipp som väntar på omförsök raderas inte: det ska hinna få ett försök mot en
// server som svarar. Därför hoppas hela sessionen över om något av dess klipp är i
// backoff (senare klipp i sessionen har då inte försökts). `now` är samma tid som
// varvets urval, annars kan en backoff som löpt ut under varvet se ut som ett försök.
// Klarmarkerade sessioner som blir tomma avslutas sedan av tryCompleteSession;
// övriga gamla, tomma lokala sessioner (t.ex. avbruten inspelning) raderas här.
async function expireOldChunks(now) {
  const cutoff = now - LOCAL_MAX_AGE_MS;
  const chunks = await getUnconfirmedChunks();
  const retrying = new Set();
  for (const c of chunks) {
    if (c.upload_state === 'pending' && c.next_attempt_at > now) retrying.add(c.client_session_id);
  }
  const expiredBySession = new Map();
  for (const c of chunks) {
    if (!c.created_at || c.created_at >= cutoff) continue;
    if (retrying.has(c.client_session_id)) continue;
    if (!expiredBySession.has(c.client_session_id)) expiredBySession.set(c.client_session_id, []);
    expiredBySession.get(c.client_session_id).push(c);
  }

  for (const [clientSessionId, expired] of expiredBySession) {
    const session = await getSession(clientSessionId);
    const lastError = expired.map((c) => c.last_error).filter(Boolean).pop() || null;
    try {
      await apiClient.post('/api/errors/failed_chunk', {
        reason: 'expired_local_24h',
        client_session_id: clientSessionId,
        server_session_id: session?.server_session_id || null,
        owner_user_id: session?.owner_user_id || null,
        chunk_index: expired[0].chunk_index,
        chunk_indices: expired.map((c) => c.chunk_index),
        attempts: Math.max(...expired.map((c) => c.attempts || 0)),
        last_error: lastError,
        timestamp: Date.now(),
      }, { timeout: 10000 });
    } catch (err) {
      if (isAuthFailure(err.response?.status)) {
        noteLoginRequired(err);
        return;
      }
      // Servern går inte att nå eller har problem: vänta, så att raderingen inte sker
      // utan rapport och klippen hinner laddas upp när den är tillbaka.
      if (isTemporary(err.response?.status)) return;
      // Övriga fel: best effort
    }
    for (const c of expired) await deleteChunk(c.id);
  }

  // En pausad inspelning kan återupptas dygnet efter, med tom kö men levande session.
  if (isRecordingBusy()) {
    if (expiredBySession.size > 0) emit({ type: 'pending_changed' });
    return;
  }

  let removedSessions = 0;
  for (const s of await getAllSessions()) {
    if (s.status === 'finished' || !s.created_at || s.created_at >= cutoff) continue;
    const remaining = await getChunksForSession(s.client_session_id);
    if (remaining.length === 0) {
      await deleteSessionAndChunks(s.client_session_id);
      removedSessions += 1;
    }
  }

  if (expiredBySession.size > 0 || removedSessions > 0) emit({ type: 'pending_changed' });
}

// Försök avsluta en session mot servern när dess kö är tömd.
async function tryCompleteSession(clientSessionId) {
  const session = await getSession(clientSessionId);
  if (!session || session.status !== 'finished') return;

  const remaining = await getChunksForSession(clientSessionId);
  const stillPending = remaining.some((c) => c.upload_state !== 'permanent_failure');
  if (stillPending) return; // vänta tills allt (utom permanent döda) är bekräftat

  const serverId = session.server_session_id;
  if (!serverId) {
    // Alla chunkar permanent döda och ingen server-session skapades — inget att avsluta.
    if (remaining.length > 0) return;
  }

  try {
    if (serverId) {
      await apiClient.post(`/recorder/sessions/${serverId}/complete`, {});
    }
    emit({ type: 'session_completed', clientSessionId, serverSessionId: serverId || null });
    await deleteSessionAndChunks(clientSessionId);
    emit({ type: 'pending_changed' });
  } catch (err) {
    if (isAuthFailure(err.response?.status)) {
      noteLoginRequired(err);
    } else if (err.response?.status === 404) {
      // Server-sessionen finns inte längre (t.ex. klarmarkerad) — inget att
      // avsluta. Städa lokalt i stället för att försöka igen för evigt.
      await deleteSessionAndChunks(clientSessionId);
      emit({ type: 'pending_changed' });
    }
    // Annars (server nere) — försök igen nästa varv.
  }
}

async function drainOnce() {
  // Även medan vi väntar på inloggning: en inspelning kan pågå, och dess ägare
  // ska vara den som spelar in, inte den som loggar in härnäst.
  const foreign = await claimSessions();
  if (waitingForLogin()) {
    emit({ type: 'login_required' });
    return;
  }

  const chunks = await getUnconfirmedChunks();
  const now = Date.now();

  // Gruppera per session, behåll ordning. Hoppa över permanent döda.
  const bySession = new Map();
  for (const c of chunks) {
    if (foreign.has(c.client_session_id)) continue;
    if (c.upload_state === 'permanent_failure') continue;
    if (c.next_attempt_at && c.next_attempt_at > now) continue;
    if (!bySession.has(c.client_session_id)) bySession.set(c.client_session_id, []);
    bySession.get(c.client_session_id).push(c);
  }

  // Ladda upp sessioner parallellt, men chunkar inom en session i ordning
  // (stoppa sessionen vid första felet så ordningen bevaras).
  const sessionIds = [...bySession.keys()];
  await Promise.all(
    sessionIds.map(async (sid) => {
      for (const chunk of bySession.get(sid)) {
        try {
          await uploadOne(chunk);
        } catch (_) {
          return; // övergående fel — avbryt denna session, behåll ordning
        }
      }
    })
  );
  if (waitingForLogin()) return;

  // Efter uppladdningsförsöket: det som fortfarande inte kommit fram efter 24 h raderas.
  await expireOldChunks(now);
  if (waitingForLogin()) return;

  // Försök avsluta ALLA färdigmarkerade sessioner vars kö nu är tömd — inte bara
  // de som hade chunkar i detta varv (en session kan ha markerats klar efter att
  // dess sista chunk redan bekräftats, och skulle annars aldrig completas/städas).
  const allSessions = await getAllSessions();
  for (const s of allSessions) {
    if (s.status === 'finished' && !foreign.has(s.client_session_id)) {
      await tryCompleteSession(s.client_session_id);
    }
  }
}

// Kör en drain; om en wake kom in under tiden, kör igen (single-flight).
async function runLoop() {
  if (running) { wantAnother = true; return; }
  running = true;
  try {
    do {
      wantAnother = false;
      await drainOnce();
    } while (wantAnother);
  } finally {
    running = false;
  }
}

// Väck uppladdaren (ny chunk, manuell "skicka igen", online-event).
export function wakeUploader() {
  runLoop().catch((e) => console.error('uploader runLoop error', e));
}

// "Slutför nu": försök om direkt, även klipp som gett upp (permanent_failure),
// och hoppa över pågående backoff-väntan. Bara inom 24 h-fönstret; äldre klipp
// städas av expireOldChunks.
export async function retryNow() {
  const cutoff = Date.now() - LOCAL_MAX_AGE_MS;
  const foreign = await claimSessions();
  for (const c of await getUnconfirmedChunks()) {
    if (foreign.has(c.client_session_id)) continue;
    if (c.upload_state === 'uploading') continue;
    if (c.created_at && c.created_at < cutoff) continue;
    if (c.upload_state === 'permanent_failure') {
      await updateChunk(c.id, { upload_state: 'pending', attempts: 0, next_attempt_at: 0 });
    } else if (c.next_attempt_at) {
      await updateChunk(c.id, { next_attempt_at: 0 });
    }
  }
  emit({ type: 'pending_changed' });
  wakeUploader();
}

let started = false;
// Montera uppladdaren globalt (t.ex. vid app-start). Idempotent; redan startad
// väcks den bara, så att klipp som väntat på inloggning går iväg direkt efter den.
export function startUploader() {
  if (started) {
    wakeUploader();
    return;
  }
  started = true;
  window.addEventListener('online', wakeUploader);
  timer = setInterval(wakeUploader, TICK_MS);
  wakeUploader(); // återuppta leftover-chunkar direkt
}

export function stopUploader() {
  if (timer) clearInterval(timer);
  window.removeEventListener('online', wakeUploader);
  timer = null;
  started = false;
}
